import ReactGA from 'react-ga4'

// Track whether GA has been successfully initialized
let isGAInitialized = false

/**
 * Initialize Google Analytics 4
 * This should be called once when the application starts
 * If initialization fails, the app will continue to work normally
 */
export const initGA = () => {
  const measurementId = import.meta.env.VITE_GOOGLE_ANALYTICS_MEASUREMENT_ID

  if (!measurementId) {
    console.error('[GA] Measurement ID not found in environment variables')
    return
  }

  try {
    ReactGA.initialize(measurementId, {
      gtagOptions: {
        // Disable automatic page view tracking - we'll handle it manually
        send_page_view: false,
      },
    })

    isGAInitialized = true
    console.log('[GA] Google Analytics initialized successfully')
  } catch (error) {
    console.error('[GA] Failed to initialize Google Analytics:', error)
    isGAInitialized = false
  }
}

/**
 * Track a page view
 * @param path - The page path (e.g., '/about-us')
 * @param title - Optional page title (defaults to document.title)
 */
export const trackPageView = (path: string, title?: string) => {
  if (!isGAInitialized) {
    return
  }

  try {
    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: title ?? document.title,
    })
    console.log(`[GA] Page view tracked: ${path}`)
  } catch (error) {
    console.error('[GA] Failed to track page view:', error)
  }
}

/**
 * Track a custom event
 * @param category - Event category (e.g., 'User', 'Social', 'Communication')
 * @param action - Event action (e.g., 'login', 'send_message')
 * @param label - Optional event label
 * @param value - Optional numeric value
 */
export const trackEvent = (category: string, action: string, label?: string, value?: number) => {
  if (!isGAInitialized) {
    return
  }

  try {
    ReactGA.event({
      category,
      action,
      label,
      value,
    })
    console.log(`[GA] Event tracked: ${category} - ${action}${label ? ` (${label})` : ''}`)
  } catch (error) {
    console.error('[GA] Failed to track event:', error)
  }
}
